import { Desc } from '@sm/intl';
import { Packages, PackageIds } from '~shared/constants/pricing';
import PACKAGE_COPY from '~pricing/experiments/grow_2792_pnp_refresh_v1/copy/experimentPackageCopy';
import STANDARD_PACKAGE_COPY from '~pricing/copy';
import { PackageDef } from '~pricing/lib/packages/types';
import { PackageType } from '~shared/types/pricing/package';
import { LegaleseDisclaimer } from '~pricing/components/PricingFooter/config/disclaimers';
import { EXPERIMENT_IDENTIFIERS } from '~server/routes/Pricing/experimentsConfig';
import PAID_FEATURES_COPY from '~pricing/experiments/grow_2792_pnp_refresh_v1/copy/experimentSummaryCopy';
import ORIGINAL_FEATURES_COPY from '~pricing/lib/PackageFeatures/details/copy';
import { PaidFeatures } from './types';

export const TogglePackages = {
  [EXPERIMENT_IDENTIFIERS.GROW_2792.treatment1]: [
    Packages.AdvantageMonthly,
    Packages.AdvantageAnnual,
    Packages.TeamAdvantage,
    Packages.FormsAnnual,
    Packages.FormsMonthly,
  ],
  // PnP refresh no forms monthly variant
  [EXPERIMENT_IDENTIFIERS.GROW_2792.treatment2]: [
    Packages.AdvantageMonthly,
    Packages.AdvantageAnnual,
    Packages.TeamAdvantage,
  ],
  // "No Forms at all: Free, Adv (Monthly + Annual), Premier, Enterprise"
  [EXPERIMENT_IDENTIFIERS.GROW_2792.treatment3]: [
    Packages.AdvantageMonthly,
    Packages.AdvantageAnnual,
    Packages.TeamAdvantage,
  ],
};

export const PACKAGE_SWAP = {
  [Packages.FormsAnnual]: {
    id: PackageIds.FORMS_ANNUAL,
    swap: {
      packageName: Packages.FormsMonthly,
      id: PackageIds.FORMS_MONTHLY,
    },
  },
  [Packages.FormsMonthly]: {
    id: PackageIds.FORMS_MONTHLY,
    swap: {
      packageName: Packages.FormsAnnual,
      id: PackageIds.FORMS_ANNUAL,
    },
  },
  [Packages.AdvantageAnnual]: {
    id: PackageIds.ADVANTAGE_ANNUAL,
    swap: {
      packageName: Packages.AdvantageMonthly,
      id: PackageIds.ADVANTAGE_MONTHLY,
    },
  },
  [Packages.AdvantageMonthly]: {
    id: PackageIds.ADVANTAGE_MONTHLY,
    swap: {
      packageName: Packages.AdvantageAnnual,
      id: PackageIds.ADVANTAGE_ANNUAL,
    },
  },
  [Packages.TeamAdvantage]: {
    id: PackageIds.TEAM_ADVANTAGE_ANNUAL,
    swap: {
      packageName: Packages.AdvantageMonthly,
      id: PackageIds.ADVANTAGE_MONTHLY,
    },
  },
};

export const PackageDefs: PackageDef = {
  [Packages.AdvantageMonthly]: {
    highlighted: true,
  },
  [Packages.TeamAdvantage]: {
    highlighted: true,
  },
};

export const PackageDisplayNames: Partial<Record<Packages, Desc>> = {
  [Packages.Basic]: STANDARD_PACKAGE_COPY.BASIC,
  [Packages.AdvantageMonthly]: PACKAGE_COPY.ADVANTAGE_MONTHLY,
  [Packages.AdvantageAnnual]: PACKAGE_COPY.ADVANTAGE_ANNUAL,
  [Packages.TeamAdvantage]: PACKAGE_COPY.TEAM_ADVANTAGE,
  [Packages.PremierAnnual]: PACKAGE_COPY.PREMIER_ANNUAL,
  [Packages.TeamPremier]: PACKAGE_COPY.TEAM_PREMIER,
};

export const GROW_2792_DISCLAIMERS = {
  [PackageType.Teams]: [LegaleseDisclaimer.OverageCost, LegaleseDisclaimer.EnterpriseAddons],
  [PackageType.Individual]: [LegaleseDisclaimer.OverageCost],
  [PackageType.Educational]: [LegaleseDisclaimer.OverageCost],
};

export const PAID_TBYB_FEATURES_MAP: Record<PaidFeatures, string> = {
  [PaidFeatures.RANDOM_ASSIGNMENT]: 'create_random_assignment_enabled',
  [PaidFeatures.THEME_LAYOUT]: 'create_custom_theme_enabled',
  [PaidFeatures.BLOCK_RANDOMIZATION]: 'multiple_block_rotation_enabled',
  [PaidFeatures.FUNNELING]: 'funneling_enabled',
  [PaidFeatures.LOGO]: 'create_logo_enabled',
  [PaidFeatures.FOOTER]: 'create_toggle_footer_enabled',
  [PaidFeatures.PAGESKIPLOGIC]: 'create_skip_logic_enabled',
  [PaidFeatures.PAGE_RANDOMIZATION]: 'create_randomization_enabled',
  [PaidFeatures.QUESTION_RANDOMIZATION]: 'create_randomization_enabled',
  [PaidFeatures.QUOTAS]: 'create_quotas_enabled',
  [PaidFeatures.CUSTOM_VARIABLES]: 'create_custom_variables_enabled',
  [PaidFeatures.THEMES]: 'create_custom_theme_enabled',
  [PaidFeatures.QUESTIONSKIP_LOGIC]: 'create_skip_logic_enabled',
  [PaidFeatures.PIPING]: 'create_piping_enabled',
  [PaidFeatures.QUESTION_MATRIX]: 'matrix_question_type',
  [PaidFeatures.QUESTION_MENU_MATRIX]: 'menu_matrix_enabled',
  [PaidFeatures.QUESTION_FILE_UPLOAD]: 'file_upload',
  [PaidFeatures.MULTILINGUAL]: 'multilingual',
  [PaidFeatures.QUIZZES_PRO]: 'quizzes_pro',
  [PaidFeatures.QUESTION_PAYMENT]: 'payment_qt',
  [PaidFeatures.QUESTION_CLICK_MAP]: 'click_map_qt',
  [PaidFeatures.FOOTER_GRAYSCALE]: 'create_grayscale_footer',
  [PaidFeatures.PREMIUM_THEMES]: 'premium_themes',
  [PaidFeatures.CUSTOM_AVATAR]: 'create_custom_chatbot_avatar',
  [PaidFeatures.COMMENT_BOX]: 'comment_box_question_type',
  [PaidFeatures.CONTACT_INFO]: 'collect_contact_information',
  [PaidFeatures.MULTIPLE_TEXTBOXES]: 'textbox_multiple_question_type',
  [PaidFeatures.RANKING]: 'ranking_question_type',
  [PaidFeatures.SLIDER]: 'slider_question_type',
  [PaidFeatures.STAR_RATING_SCALE]: 'star_rating_question_type',
  [PaidFeatures.QUESTION_BEST_WORST]: 'best_worst_qt',
  [PaidFeatures.QUESTION_LIMIT]: 'create_question_limit',
  [PaidFeatures.ADVANCED_BRANCHING]: 'advanced_branching',
};

export const PAID_TBYB_FEATURES_COPY_MAP: Record<string, { includedPackage: Packages[]; label: Desc }> = {
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.RANDOM_ASSIGNMENT]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: PAID_FEATURES_COPY.grow_2792_create_random_assignment_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.THEME_LAYOUT]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.premium_themes,
  }, // Using Preimum_themes copy for theme_layout as in UCS
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.BLOCK_RANDOMIZATION]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: ORIGINAL_FEATURES_COPY.multiple_block_rotation_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.FUNNELING]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: PAID_FEATURES_COPY.grow_2792_funneling_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.LOGO]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.create_logo_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.FOOTER]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: ORIGINAL_FEATURES_COPY.create_toggle_footer_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.PAGESKIPLOGIC]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.create_skip_logic_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.PAGE_RANDOMIZATION]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: PAID_FEATURES_COPY.grow_2792_ippm_create_randomization_enabled_page,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUESTION_RANDOMIZATION]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: PAID_FEATURES_COPY.grow_2792_ippm_create_randomization_enabled_question,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUOTAS]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: ORIGINAL_FEATURES_COPY.create_quotas_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.CUSTOM_VARIABLES]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: ORIGINAL_FEATURES_COPY.create_custom_variables_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.THEMES]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: PAID_FEATURES_COPY.grow_2792_ippm_create_custom_theme_enabled,
  }, // Using Preimum_themes copy for custom_themes as in UCS
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUESTIONSKIP_LOGIC]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.create_skip_logic_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.PIPING]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: ORIGINAL_FEATURES_COPY.create_piping_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUESTION_MATRIX]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: PAID_FEATURES_COPY.grow_2792_ippm_matrix_question_type,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUESTION_MENU_MATRIX]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.menu_matrix_enabled,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUESTION_FILE_UPLOAD]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: PAID_FEATURES_COPY.grow_2792_ippm_file_upload,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.MULTILINGUAL]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.multilingual,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUIZZES_PRO]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.quizzes_pro,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUESTION_PAYMENT]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.payment_qt,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUESTION_CLICK_MAP]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: ORIGINAL_FEATURES_COPY.click_map_qt,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.FOOTER_GRAYSCALE]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.create_grayscale_footer,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.PREMIUM_THEMES]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.premium_themes,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.CUSTOM_AVATAR]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
    ],
    label: PAID_FEATURES_COPY.grow_2792_create_custom_chatbot_avatar,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.COMMENT_BOX]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
    ],
    label: PAID_FEATURES_COPY.grow_2792_comment_box_question_type,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.CONTACT_INFO]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.collect_contact_information,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.MULTIPLE_TEXTBOXES]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
    ],
    label: PAID_FEATURES_COPY.grow_2792_textbox_multiple_question_type,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.RANKING]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.ranking_question_type,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.SLIDER]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: ORIGINAL_FEATURES_COPY.slider_question_type,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.STAR_RATING_SCALE]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: PAID_FEATURES_COPY.grow_2792_star_rating_question_type,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUESTION_BEST_WORST]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
    ],
    label: PAID_FEATURES_COPY.grow_2792_ippm_best_worst_scale,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.QUESTION_LIMIT]]: {
    includedPackage: [
      Packages.Enterprise,
      Packages.StandardMonthly,
      Packages.StandardAnnual,
      Packages.AdvantageAnnual,
      Packages.PremierAnnual,
      Packages.TeamAdvantage,
      Packages.TeamPremier,
      Packages.AdvantageMonthly,
      Packages.FormsMonthly,
      Packages.FormsAnnual,
    ],
    label: PAID_FEATURES_COPY.grow_2792_ippm_create_question_limit,
  },
  [PAID_TBYB_FEATURES_MAP[PaidFeatures.ADVANCED_BRANCHING]]: {
    includedPackage: [Packages.Enterprise, Packages.PremierAnnual, Packages.TeamPremier],
    label: PAID_FEATURES_COPY.grow_2792_advanced_branching,
  },
};
